/**
 * Theta Lake's "server-initiated OAuth 2.0" hands us a `parameter_token` before the
 * standard authorization-code flow can start (DEV-2843).
 *
 * Sequence: a Theta Lake Portal user enables our application, their server redirects the
 * browser to our registered Login URL carrying `state` + `parameter_token`, we authenticate
 * the user (our Integrations page already requires login), and then the normal Connect flow
 * runs with `parameter_token` appended to the authorize redirect.
 *
 * Stored in sessionStorage on purpose. It is short-lived, only needed to reach the authorize
 * step, and carries identifiers rather than authorization — the real access and refresh
 * tokens still live server-side in the UpContent database like every other integration.
 * Consequence to be aware of: it does not survive a cleared cache or a different device, so
 * a user in that position has to Enable again. An account that is *already connected* is
 * unaffected, because nothing after the token exchange needs this value.
 */

const STORAGE_KEY = 'thetalake_parameter_token';
const STATE_KEY = 'thetalake_login_state';

function storage() {
    try {
        return window.sessionStorage || null;
    } catch (e) {
        // Private browsing modes can throw on access rather than returning null.
        console.warn('sessionStorage unavailable; Theta Lake Enable flow will not persist', e);
        return null;
    }
}

export function setParameterToken(token, state) {
    const s = storage();
    if (!s || !token) return false;
    try {
        s.setItem(STORAGE_KEY, token);
        if (state) s.setItem(STATE_KEY, state);
        return true;
    } catch (e) {
        console.warn('could not store Theta Lake parameter_token', e);
        return false;
    }
}

export function getParameterToken() {
    const s = storage();
    if (!s) return null;
    try {
        return s.getItem(STORAGE_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function getLoginState() {
    const s = storage();
    if (!s) return null;
    try {
        return s.getItem(STATE_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function hasParameterToken() {
    return !!getParameterToken();
}

/**
 * Clear after a successful connect. The token is single-purpose and keeping it around would
 * make the button read "Connect" for an account that is already connected.
 */
export function clearParameterToken() {
    const s = storage();
    if (!s) return;
    try {
        s.removeItem(STORAGE_KEY);
        s.removeItem(STATE_KEY);
    } catch (e) {
        // Nothing useful to do; a stale token only affects button labelling.
    }
}
